/**
 * DocHub V2 sheet checkboxes — indexed per sheet, row-major (left→right, top→bottom).
 * Stored as documentState.sheetCheckboxes[sheetName] = boolean[]
 */

import { expandSheetForCustomTables } from './expandSheetForCustomTables';

export const SHEET_CHECKBOXES_FIELD = 'sheetCheckboxes';

export const isCheckboxCell = (sheet, row0, col0) => {
  if (sheet?.checkboxes?.[row0]?.[col0]) return true;
  if (sheet?.cellKinds?.[row0]?.[col0] === 'checkbox') return true;
  const raw = sheet?.values?.[row0]?.[col0];
  if (typeof raw === 'boolean') return true;
  const display = sheet?.displayValues?.[row0]?.[col0];
  return display === 'TRUE' || display === 'FALSE';
};

const defaultCheckedAt = (sheet, row0, col0) => {
  const raw = sheet?.values?.[row0]?.[col0];
  const display = sheet?.displayValues?.[row0]?.[col0];
  return raw === true || display === 'TRUE' || display === '✓' || display === '☑';
};

/** All checkbox cells on a sheet in stable print/UI order (includes hidden rows/cols). */
export const listSheetCheckboxes = sheet => {
  if (!sheet) return [];

  const rows = Math.max(
    sheet.rows || 0,
    sheet.checkboxes?.length || 0,
    sheet.values?.length || 0,
    sheet.cellKinds?.length || 0
  );

  const list = [];
  for (let row0 = 0; row0 < rows; row0++) {
    const cols = Math.max(
      sheet.cols || 0,
      sheet.checkboxes?.[row0]?.length || 0,
      sheet.values?.[row0]?.length || 0,
      sheet.cellKinds?.[row0]?.length || 0
    );
    for (let col0 = 0; col0 < cols; col0++) {
      if (!isCheckboxCell(sheet, row0, col0)) continue;
      list.push({
        index: list.length,
        row0,
        col0,
        row1: row0 + 1,
        col1: col0 + 1,
        defaultChecked: defaultCheckedAt(sheet, row0, col0)
      });
    }
  }
  return list;
};

/** Map "row0,col0" → checkbox index for a sheet. */
export const buildCheckboxIndexMap = sheet => {
  const map = {};
  listSheetCheckboxes(sheet).forEach(cell => {
    map[`${cell.row0},${cell.col0}`] = cell;
  });
  return map;
};

export const getSheetCheckboxValue = (documentState, sheetName, index, defaultChecked = false) => {
  const arr = documentState?.[SHEET_CHECKBOXES_FIELD]?.[sheetName];
  if (Array.isArray(arr) && index < arr.length && typeof arr[index] === 'boolean') {
    return arr[index];
  }
  return !!defaultChecked;
};

export const setSheetCheckboxValue = (documentState, sheetName, index, checked, defaults = []) => {
  const prevRoot =
    documentState?.[SHEET_CHECKBOXES_FIELD] &&
    typeof documentState[SHEET_CHECKBOXES_FIELD] === 'object'
      ? documentState[SHEET_CHECKBOXES_FIELD]
      : {};
  const prevArr = Array.isArray(prevRoot[sheetName]) ? [...prevRoot[sheetName]] : [];
  const size = Math.max(prevArr.length, defaults.length, index + 1);
  const nextArr = Array.from({ length: size }, (_, i) => {
    if (i === index) return !!checked;
    if (i < prevArr.length && typeof prevArr[i] === 'boolean') return prevArr[i];
    return !!defaults[i];
  });

  return {
    ...documentState,
    [SHEET_CHECKBOXES_FIELD]: {
      ...prevRoot,
      [sheetName]: nextArr
    }
  };
};

/**
 * Seed checkbox arrays from layout (+ expand) overlaid with saved CRM values.
 * `saved` is selectedGroup.sheetCheckboxes or inventory.sheetCheckboxes.
 */
export const initSheetCheckboxes = (documentLayout, saved = null, documentState = {}) => {
  const result = {};
  const savedRoot = saved && typeof saved === 'object' && !Array.isArray(saved) ? saved : {};

  (documentLayout?.sheets || []).forEach(sheet => {
    if (sheet.hidden) return;
    const expanded = expandSheetForCustomTables(sheet, documentState);
    const list = listSheetCheckboxes(expanded);
    if (!list.length) return;

    const prev = savedRoot[sheet.name];
    result[sheet.name] = list.map((cell, i) =>
      Array.isArray(prev) && typeof prev[i] === 'boolean' ? prev[i] : cell.defaultChecked
    );
  });

  return result;
};

/** Full snapshot for save/print — every sheet that has checkboxes. */
export const snapshotSheetCheckboxes = (documentState, documentLayout) => {
  const result = {};

  (documentLayout?.sheets || []).forEach(sheet => {
    if (sheet.hidden) return;
    const expanded = expandSheetForCustomTables(sheet, documentState);
    const list = listSheetCheckboxes(expanded);
    if (!list.length) return;

    const saved = documentState?.[SHEET_CHECKBOXES_FIELD]?.[sheet.name];
    result[sheet.name] = list.map((cell, i) =>
      Array.isArray(saved) && typeof saved[i] === 'boolean' ? saved[i] : cell.defaultChecked
    );
  });

  return result;
};

/** Merge saved sheetCheckboxes onto payload without dropping other sheets. */
export const mergeSheetCheckboxesForSave = (nextSnapshot, existingSaved) => {
  const existing =
    existingSaved && typeof existingSaved === 'object' && !Array.isArray(existingSaved)
      ? existingSaved
      : {};
  return {
    ...existing,
    ...nextSnapshot
  };
};
